import { Checkbox } from '../../ui/checkbox';

export interface QuoteLine {
	id: string;
	name: string;
	sku?: string | null;
	/** The tier quantities the supplier will be asked to price, smallest first. */
	quantities: number[];
}

/**
 * Which quote lines a supplier link covers. At least one — the caller disables
 * sending on an empty selection rather than this blocking the last untick.
 */
export function QuoteLinePicker({
	lines,
	value,
	onChange,
	disabled = false,
}: {
	lines: QuoteLine[];
	value: string[];
	onChange: (ids: string[]) => void;
	disabled?: boolean;
}) {
	const toggle = (id: string, on: boolean) =>
		onChange(on ? [...value, id] : value.filter((v) => v !== id));

	if (lines.length === 0) {
		return <div className="text-sm text-text-4">This quote has no products yet.</div>;
	}

	return (
		<div className="flex flex-col divide-y divide-line-1 rounded-[var(--r-3)] border border-line-1">
			{lines.map((line) => {
				const checked = value.includes(line.id);
				return (
					<label
						key={line.id}
						className="flex cursor-pointer items-start gap-2.5 px-3 py-2.5 transition-colors hover:bg-surface-hover"
					>
						<Checkbox
							className="mt-0.5"
							checked={checked}
							onCheckedChange={(on) => toggle(line.id, on)}
							disabled={disabled}
						/>
						<div className="min-w-0 flex-1">
							<div className="truncate text-sm text-text-1">{line.name}</div>
							<div className="text-xs text-text-4">
								{line.sku ? `${line.sku} · ` : ''}
								{line.quantities.length > 0
									? line.quantities
											.map((q) => q.toLocaleString('en-US'))
											.join(' / ') + ' pcs'
									: 'No quantities yet'}
							</div>
						</div>
					</label>
				);
			})}
			{value.length === 0 && (
				<div className="px-3 py-2 text-sm text-status-warn">Pick at least one product.</div>
			)}
		</div>
	);
}
